import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LogOut, Menu, User } from 'lucide-react';
import { Brand } from '../ui/Brand.jsx';
import { ConfirmDialog } from '../ui/ConfirmDialog.jsx';
import { selectCanShowSettings } from '../features/appVisibility/store/selectors.js';
import { signedOut } from '../features/auth/store/authSlice.js';
import { isNativePlatform } from '../services/native/platformService.js';
import { tapHaptic } from '../services/native/hapticsService.js';

const loadMobileNavDrawer = () => import('./MobileNavDrawer.jsx');
const loadNativeAccountSheet = () => import('./NativeAccountSheet.jsx');

const MobileNavDrawer = lazy(() => loadMobileNavDrawer().then((module) => ({
  default: module.MobileNavDrawer,
})));

const NativeAccountSheet = lazy(() => loadNativeAccountSheet().then((module) => ({
  default: module.NativeAccountSheet,
})));

export function MobileTopBar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const canShowSettings = useSelector(selectCanShowSettings);
  const native = isNativePlatform();
  const nativeCourses = native && location.pathname === '/courses';
  const topBarIconSize = nativeCourses ? 26 : 20;
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [hasOpenedNav, setHasOpenedNav] = useState(false);
  const [hasOpenedAccount, setHasOpenedAccount] = useState(false);
  const closeNav = useCallback(() => setOpen(false), []);
  const closeAccountForDelete = useCallback(() => {
    setAccountOpen(false);
    setHasOpenedAccount(false);
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (native) {
        void loadNativeAccountSheet();
      } else {
        void loadMobileNavDrawer();
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [native]);

  useEffect(() => {
    setOpen(false);
    setAccountOpen(false);
    setSignOutOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      <div className={`lg:hidden grid grid-cols-[1fr_auto_1fr] items-center px-4 py-3 border-b border-line bg-paper z-10 ${native ? 'native-top-bar relative' : 'sticky top-0'}`}>
        {native ? (
          <span aria-hidden="true" />
        ) : (
          <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);
              if (nextOpen) setHasOpenedNav(true);
            }}
          >
            <Dialog.Trigger asChild>
              <button
                type="button"
                aria-label="Open navigation"
                className="w-9 h-9 inline-flex items-center justify-center rounded-input hover:bg-line-2 transition-colors duration-color"
              >
                <Menu aria-hidden size={topBarIconSize} strokeWidth={2} />
              </button>
            </Dialog.Trigger>
            <Suspense fallback={null}>
              {hasOpenedNav && <MobileNavDrawer open={open} onNavigate={closeNav} />}
            </Suspense>
          </Dialog.Root>
        )}

        <Brand height={native ? 48 : 26} />

        <div className="flex items-center gap-1 justify-self-end">
          {native && canShowSettings ? (
            <Dialog.Root
              open={accountOpen}
              onOpenChange={(nextOpen) => {
                setAccountOpen(nextOpen);
                if (nextOpen) {
                  setHasOpenedAccount(true);
                  void tapHaptic();
                }
              }}
            >
              <Dialog.Trigger asChild>
                <button
                  type="button"
                  aria-label="Open account"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-input hover:bg-line-2 transition-colors duration-color text-ink"
                >
                  <User aria-hidden size={topBarIconSize} strokeWidth={1.9} />
                </button>
              </Dialog.Trigger>
              <Suspense fallback={null}>
                {hasOpenedAccount && (
                  <NativeAccountSheet
                    open={accountOpen}
                    onOpenChange={setAccountOpen}
                    onDeleteAccount={closeAccountForDelete}
                  />
                )}
              </Suspense>
            </Dialog.Root>
          ) : null}
          {native && !canShowSettings ? (
            <button
              type="button"
              aria-label="Sign out"
              className="w-9 h-9 inline-flex items-center justify-center rounded-input hover:bg-line-2 transition-colors duration-color text-ink"
              onClick={() => {
                setSignOutOpen(true);
                void tapHaptic();
              }}
            >
              <LogOut aria-hidden size={topBarIconSize} strokeWidth={1.9} />
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={signOutOpen}
        tone="danger"
        title="Sign out?"
        description="You will need to sign in again to continue your courses and assessments."
        cancelLabel="Stay signed in"
        confirmLabel="Sign out"
        onCancel={() => setSignOutOpen(false)}
        onConfirm={() => {
          setSignOutOpen(false);
          dispatch(signedOut());
        }}
      />
    </>
  );
}
